import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Modal, Stack, Text, Group, Checkbox, Loader, Alert, Button } from '@mantine/core';
import { CH, I, IconBtn } from './MobileAppHeader';
import { api } from '../../apiClient';
import { isAdmin } from '../../utils/authUtils';
import QuestionComments from '../QuestionComments';

const SCRAPER_API_URL = process.env.REACT_APP_SCRAPER_API_URL || 'http://localhost:8000';

function MobileQuestionView({
  currentQuestion,
  currentIndex,
  totalQuestions,
  results,
  onSetResult,
  onNext,
  onPrev,
  onImageClick,
  showAnswer,
  toggleShowAnswer,
  onSetCurrentQuestion,
  selectedCertification,
  questionOrder,
  onBackToFilter,
  statusLabelMap,
  user,
}) {
  const navigate = useNavigate();
  const questionId = currentQuestion.id;
  const [questionHtml, setQuestionHtml] = useState(null);
  const [btQuestionHtml, setBtQuestionHtml] = useState('');
  const [answerHtml, setAnswerHtml] = useState('');
  const [btAnswerHtml, setBtAnswerHtml] = useState('');
  const [descriptionHtml, setDescriptionHtml] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);

  // 再処理モーダル
  const [rescrapeModalOpen, setRescrapeModalOpen] = useState(false);
  const [rescrapeOptions, setRescrapeOptions] = useState(['full']);
  const [rescrapeStep, setRescrapeStep] = useState(0);
  const [rescrapeTotalSteps, setRescrapeTotalSteps] = useState(0);
  const [isRescraping, setIsRescraping] = useState(false);
  const [rescrapeJobStatus, setRescrapeJobStatus] = useState(null);
  const [rescrapeError, setRescrapeError] = useState(null);
  const rescrapeIntervalRef = useRef(null);

  useEffect(() => {
    if (!questionId) return;
    setQuestionHtml(null);
    setBtQuestionHtml('');
    setAnswerHtml('');
    setBtAnswerHtml('');
    setDescriptionHtml(null);
    api.get(`/questions/${questionId}/html`)
      .then((data) => {
        setQuestionHtml(data?.question ?? '');
        setBtQuestionHtml(data?.bt_question ?? '');
        setAnswerHtml(data?.answer ?? '');
        setBtAnswerHtml(data?.bt_answer ?? '');
      })
      .catch(() => setQuestionHtml(''));
  }, [questionId]);

  useEffect(() => {
    isAdmin(user).then(setIsAdminUser);
  }, [user]);

  useEffect(() => () => {
    if (rescrapeIntervalRef.current) clearInterval(rescrapeIntervalRef.current);
  }, []);

  const toggleRescrapeOption = (value) => {
    if (value === 'full') { setRescrapeOptions(['full']); return; }
    setRescrapeOptions(prev => {
      const without = prev.filter(m => m !== 'full');
      return without.includes(value) ? without.filter(m => m !== value) : [...without, value];
    });
  };

  const getOrderedModes = (options) => {
    if (options.includes('full')) return ['full'];
    return ['scrape_translate', 'translate_only', 'description_only'].filter(m => options.includes(m));
  };

  const handleRescrape = async () => {
    const orderedModes = getOrderedModes(rescrapeOptions);
    if (orderedModes.length === 0) return;
    setIsRescraping(true);
    setRescrapeJobStatus('running');
    setRescrapeError(null);
    setRescrapeStep(0);
    setRescrapeTotalSteps(orderedModes.length);
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens?.idToken?.toString();
      if (!token) throw new Error('ログインが必要です');
      for (let i = 0; i < orderedModes.length; i++) {
        setRescrapeStep(i + 1);
        const res = await fetch(`${SCRAPER_API_URL}/scraper/questions/rescrape`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ question_id: questionId, mode: orderedModes[i] }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || '再処理の起動に失敗しました'); }
        const { job_id: jobId } = await res.json();
        await new Promise((resolve, reject) => {
          rescrapeIntervalRef.current = setInterval(async () => {
            try {
              const jobData = await api.get('/scrape-jobs', { id: jobId });
              if (jobData?.status === 'completed') { clearInterval(rescrapeIntervalRef.current); resolve(); }
              else if (jobData?.status === 'failed') { clearInterval(rescrapeIntervalRef.current); reject(new Error('再処理が失敗しました')); }
            } catch {}
          }, 3000);
        });
      }
      setRescrapeJobStatus('success');
      setIsRescraping(false);
      setTimeout(() => {
        setRescrapeModalOpen(false);
        setRescrapeJobStatus(null);
        setRescrapeStep(0);
        api.get(`/questions/${questionId}/html`).then((d) => {
          setQuestionHtml(d?.question ?? '');
          setBtQuestionHtml(d?.bt_question ?? '');
          setAnswerHtml(d?.answer ?? '');
          setBtAnswerHtml(d?.bt_answer ?? '');
          if (d?.description !== undefined) setDescriptionHtml(d.description ?? '');
        }).catch(() => {});
      }, 1500);
    } catch (err) {
      if (rescrapeIntervalRef.current) clearInterval(rescrapeIntervalRef.current);
      setRescrapeJobStatus('error');
      setRescrapeError(err.message);
      setIsRescraping(false);
    }
  };

  const getCurrentStatus = () => {
    const r = results[questionId];
    if (!r) return '未回答';
    return statusLabelMap?.[r] ?? r;
  };

  const statusColor = { correct: '#099268', incorrect: '#c03727', excluded: '#c99a2e' }[results[questionId]] || CH.muted;

  return (
    <>
      {/* 再処理モーダル */}
      <Modal opened={rescrapeModalOpen} onClose={() => { if (!isRescraping) { setRescrapeModalOpen(false); setRescrapeJobStatus(null); setRescrapeError(null); } }} title="単体問題の再処理" centered>
        <Stack gap="md">
          <Text size="sm" c="dimmed">問題 ID: {questionId}</Text>
          <Stack gap="xs">
            <Text size="sm" fw={500}>処理モード</Text>
            <Checkbox label="すべて修正" description="再スクレイピング + 翻訳 + 解説生成" checked={rescrapeOptions.includes('full')} onChange={() => toggleRescrapeOption('full')} disabled={isRescraping} />
            <Text size="xs" c="dimmed" mt={2}>以下は複数選択可</Text>
            <Checkbox label="問題/解答のスクレイピング/翻訳" checked={rescrapeOptions.includes('scrape_translate')} onChange={() => toggleRescrapeOption('scrape_translate')} disabled={isRescraping} />
            <Checkbox label="翻訳のみ" checked={rescrapeOptions.includes('translate_only')} onChange={() => toggleRescrapeOption('translate_only')} disabled={isRescraping} />
            <Checkbox label="解説生成のみ" checked={rescrapeOptions.includes('description_only')} onChange={() => toggleRescrapeOption('description_only')} disabled={isRescraping} />
          </Stack>
          {rescrapeJobStatus === 'running' && <Group gap="xs"><Loader size="xs" /><Text size="sm">{rescrapeTotalSteps > 1 ? `ステップ ${rescrapeStep}/${rescrapeTotalSteps} 処理中...` : '処理中...'}</Text></Group>}
          {rescrapeJobStatus === 'success' && <Alert color="green" variant="light">再処理が完了しました。</Alert>}
          {rescrapeJobStatus === 'error' && rescrapeError && <Alert color="red" variant="light">{rescrapeError}</Alert>}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { if (!isRescraping) { setRescrapeModalOpen(false); setRescrapeJobStatus(null); setRescrapeError(null); } }} disabled={isRescraping}>キャンセル</Button>
            <Button onClick={handleRescrape} loading={isRescraping} disabled={isRescraping || rescrapeJobStatus === 'success' || rescrapeOptions.length === 0}>実行</Button>
          </Group>
        </Stack>
      </Modal>

      <div style={{ background: 'var(--mantine-color-body)', minHeight: '100vh', fontFamily: CH.serif, paddingBottom: 100 }}>
        {/* ヘッダー */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 20,
          background: 'var(--mantine-color-body)',
          borderBottom: '1px solid var(--app-color-border)',
          padding: '8px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          minHeight: 48,
        }}>
          <div style={{ display: 'flex', gap: 4 }}>
            <IconBtn onClick={() => navigate('/Study')}>{I.home}</IconBtn>
            <IconBtn onClick={onBackToFilter}>{I.back}</IconBtn>
          </div>
          {isAdminUser && (
            <IconBtn onClick={() => { setRescrapeJobStatus(null); setRescrapeError(null); setRescrapeModalOpen(true); }}>{I.refresh}</IconBtn>
          )}
        </div>

        {/* 問題メタ */}
        <div style={{ padding: '14px 16px 0' }}>
          <div style={{
            background: 'var(--app-color-surface)',
            border: '1px solid var(--app-color-border)',
            borderLeft: '3px solid #c96442',
            borderRadius: 8, padding: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: CH.ink }}>
                {questionOrder === 'last' ? totalQuestions - currentIndex : currentIndex + 1} / {totalQuestions}
                <span style={{ marginLeft: 10, fontSize: 11, color: statusColor, fontWeight: 400 }}>
                  {getCurrentStatus()}
                </span>
              </div>
              <span style={{
                background: '#fff4e6', color: '#e8590c',
                fontFamily: CH.serif, fontSize: 11, fontWeight: 500,
                padding: '2px 8px', borderRadius: 4,
              }}>{selectedCertification?.toUpperCase()}</span>
            </div>
            <div style={{ fontFamily: CH.mono, fontSize: 10, color: CH.dim, marginTop: 4 }}>
              ID: {currentQuestion.id} · Topic {currentQuestion.topic} · Q.{currentQuestion.number}
            </div>
            {/* 進捗バー */}
            <div style={{ marginTop: 8, height: 3, background: 'var(--app-color-border)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                width: `${totalQuestions > 0 ? ((currentIndex + 1) / totalQuestions) * 100 : 0}%`,
                height: '100%', background: '#c96442',
              }} />
            </div>
          </div>
        </div>

        {/* 問題文 */}
        <div style={{ padding: '14px 16px 0' }}>
          <div style={{
            background: 'var(--app-color-surface)',
            border: '1px solid var(--app-color-border)',
            borderRadius: 8, padding: 16,
          }}>
            {questionHtml === null ? (
              <div style={{ color: CH.dim, fontSize: 13 }}>読み込み中...</div>
            ) : (
              <div className="question-html-body"
                dangerouslySetInnerHTML={{ __html: questionHtml || btQuestionHtml || '' }}
                style={{ fontSize: 14, lineHeight: 1.75, color: CH.ink }}
              />
            )}
          </div>
        </div>

        {/* 解答表示ボタン */}
        <div style={{ padding: '14px 16px', textAlign: 'center' }}>
          <div onClick={toggleShowAnswer} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            height: 38, padding: '0 20px',
            background: showAnswer ? 'transparent' : '#fdf5f2',
            color: showAnswer ? CH.muted : '#c96442',
            border: showAnswer ? '1px solid var(--app-color-border)' : '1px solid #c96442',
            borderRadius: 8, fontFamily: CH.serif, fontSize: 14, fontWeight: 500,
            cursor: 'pointer',
          }}>
            {I.eye}
            {showAnswer ? '解答を隠す' : '解答を表示'}
          </div>
        </div>

        {/* 解答・解説 */}
        {showAnswer && (
          <div style={{ padding: '0 16px' }}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: CH.serif, fontSize: 14, fontWeight: 500, color: '#099268', marginBottom: 8 }}>正解</div>
              <div style={{
                background: 'var(--app-color-surface)',
                border: '1px solid var(--app-color-border)',
                borderLeft: '3px solid #099268',
                borderRadius: 8, padding: 14,
              }}>
                <div className="question-html-body"
                  dangerouslySetInnerHTML={{ __html: answerHtml || btAnswerHtml || '' }}
                  style={{ fontSize: 14, lineHeight: 1.7, color: CH.ink }}
                />
              </div>
            </div>

            {(currentQuestion.description || descriptionHtml) && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: CH.serif, fontSize: 14, fontWeight: 500, color: '#c96442', marginBottom: 8 }}>解説</div>
                <div style={{
                  background: 'var(--app-color-surface)',
                  border: '1px solid var(--app-color-border)',
                  borderLeft: '3px solid #c96442',
                  borderRadius: 8, padding: 14,
                }}>
                  <div className="question-html-body"
                    dangerouslySetInnerHTML={{ __html: descriptionHtml !== null ? descriptionHtml : (currentQuestion.description || '') }}
                    style={{ fontSize: 13, lineHeight: 1.75, color: CH.ink }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <QuestionComments questionId={questionId} user={user} />

        {/* スティッキーアクションバー */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
          background: 'var(--mantine-color-body)',
          borderTop: '1px solid var(--app-color-border)',
          padding: '10px 16px 30px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          {/* 前へ・次へ */}
          <div style={{ display: 'flex', gap: 6 }}>
            <IconBtn
              onClick={questionOrder === 'last' ? onNext : onPrev}
              style={{ background: 'var(--app-color-surface)', border: '1px solid var(--app-color-border)', width: 42, height: 42 }}
            >{I.chevL}</IconBtn>
            <IconBtn
              onClick={questionOrder === 'last' ? onPrev : onNext}
              style={{ background: 'var(--app-color-surface)', border: '1px solid var(--app-color-border)', width: 42, height: 42 }}
            >{I.chevR}</IconBtn>
          </div>

          {/* ステータスボタン */}
          <div style={{ display: 'flex', gap: 8 }}>
            <IconBtn
              onClick={() => onSetResult(questionId, 'correct')}
              style={{
                width: 42, height: 42,
                background: results[questionId] === 'correct' ? '#e6fcf5' : 'var(--app-color-surface)',
                border: `1px solid ${results[questionId] === 'correct' ? '#099268' : 'var(--app-color-border)'}`,
                color: '#099268',
              }}
              title="正解"
            >{I.check}</IconBtn>
            <IconBtn
              onClick={() => onSetResult(questionId, 'incorrect')}
              style={{
                width: 42, height: 42,
                background: results[questionId] === 'incorrect' ? '#fff0ee' : 'var(--app-color-surface)',
                border: `1px solid ${results[questionId] === 'incorrect' ? '#c03727' : 'var(--app-color-border)'}`,
                color: '#c03727',
              }}
              title="不正解"
            >{I.x}</IconBtn>
            <IconBtn
              onClick={() => onSetResult(questionId, 'excluded')}
              style={{
                width: 42, height: 42,
                background: results[questionId] === 'excluded' ? '#fff7e0' : 'var(--app-color-surface)',
                border: `1px solid ${results[questionId] === 'excluded' ? '#c99a2e' : 'var(--app-color-border)'}`,
                color: '#c99a2e',
                fontSize: 16, fontWeight: 700,
              }}
              title="除外"
            >—</IconBtn>
          </div>
        </div>
      </div>
    </>
  );
}

export default MobileQuestionView;
